/**
 * InstrumentDropdown Component
 *
 * A pre-configured dropdown for selecting instruments from the user's library.
 * Loads instruments from InstrumentService and groups them by asset class.
 * Includes an "Import from Catalog..." option that opens InstrumentImportModal.
 * Handles inactive instruments with inline reactivation via confirmation modal.
 */

// 1. External dependencies
import React, { useState, useEffect, useCallback, useMemo } from 'react';

// 2. Internal absolute imports
import { Select, SelectItem, SelectSection, type PopoverProps } from '@enfomentis/ui';
import { PackagePlus } from 'lucide-react';

// 3. Relative imports
import { ReactivateInstrumentConfirmationModal } from './ReactivateInstrumentConfirmationModal';
import { InstrumentImportModal } from './InstrumentImportModal';

// 4. Services & Types
import { instrumentService } from '@/services/InstrumentService';
import type { Instrument } from '@/types/schema/Instrument';
import type { AssetClassType } from '@/types/shared/AssetClass';

// 5. Constants
import { ASSET_CLASS_TYPE_DISPLAY_MAP } from '@/constants/assetClass';

/**
 * State for pending reactivation confirmation
 */
interface PendingReactivation {
  instrumentId: string;
  symbol: string;
  displayName?: string;
}

/**
 * Props for InstrumentDropdown
 */
export interface InstrumentDropdownProps {
  /** Selected instrument ID or null */
  value: string | null;
  /** Callback when selection changes */
  onChange: (instrumentId: string) => void;
  /** Optional label for the Select */
  label?: string;
  /** Optional placeholder text */
  placeholder?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Disabled state */
  isDisabled?: boolean;
  /** Optional className for container */
  className?: string;
  /** Disable animation (fixes aria-hidden warning when used inside Modal) */
  disableAnimation?: boolean;
  /** Popover props to pass through to Select (use portalContainer for modal contexts) */
  popoverProps?: Partial<PopoverProps>;
  /** Callback when instruments list changes (after reactivation or import) */
  onInstrumentsChanged?: () => void;
  /** Key to trigger instrument list refresh (increment to force reload) */
  refreshKey?: number;
  /** Instrument IDs to exclude from the active instruments list (e.g., already-selected instruments) */
  excludeIds?: string[];
}

/**
 * Special key for the "Import from Catalog..." option
 */
const IMPORT_KEY = '__import__';

/**
 * Asset class display order (prioritizing common trading asset classes)
 */
const ASSET_CLASS_ORDER: AssetClassType[] = [
  'futures',
  'forex',
  'stocks',
  'crypto',
  'options',
  'cfd',
  'funds',
  'bonds'
];

/**
 * InstrumentDropdown Component
 */
export const InstrumentDropdown: React.FC<InstrumentDropdownProps> = ({
  value,
  onChange,
  label,
  placeholder = 'Select instrument',
  size = 'md',
  isDisabled = false,
  className,
  disableAnimation = false,
  popoverProps,
  onInstrumentsChanged,
  refreshKey = 0,
  excludeIds,
}) => {
  // Instruments state
  const [instruments, setInstruments] = useState<Instrument[]>([]);
  const [loading, setLoading] = useState(true);

  // Reactivation modal state
  const [pendingReactivation, setPendingReactivation] = useState<PendingReactivation | null>(null);

  // Import modal state
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  /**
   * Load instruments from service
   */
  const loadInstruments = useCallback(async () => {
    try {
      setLoading(true);
      const allInstruments = await instrumentService.getAll();
      setInstruments(allInstruments);
    } catch (err) {
      console.error('Failed to load instruments:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load instruments on mount and when refreshKey changes
  useEffect(() => {
    loadInstruments();
  }, [loadInstruments, refreshKey]);

  // Separate active and inactive instruments (archived instruments are excluded - they're soft-deleted)
  const { activeInstruments, inactiveInstruments } = useMemo(() => {
    let active = instruments.filter((i) => i.status === 'active');
    if (excludeIds && excludeIds.length > 0) {
      const excludeSet = new Set(excludeIds);
      active = active.filter((i) => !excludeSet.has(i.id));
    }
    const inactive = instruments.filter((i) => i.status === 'inactive');
    return { activeInstruments: active, inactiveInstruments: inactive };
  }, [instruments, excludeIds]);

  // Group active instruments by asset class
  const groupedInstruments = useMemo(() => {
    const grouped = new Map<AssetClassType, Instrument[]>();
    for (const instrument of activeInstruments) {
      const assetClass = instrument.classification.assetClass;
      const existing = grouped.get(assetClass) || [];
      grouped.set(assetClass, [...existing, instrument]);
    }
    return grouped;
  }, [activeInstruments]);

  // Get populated asset classes in order
  const populatedAssetClasses = useMemo(() => {
    return ASSET_CLASS_ORDER.filter(
      (assetClass) => (groupedInstruments.get(assetClass)?.length ?? 0) > 0
    );
  }, [groupedInstruments]);

  /**
   * Handle reactivation of inactive instrument
   */
  const handleReactivateAndSelect = useCallback(
    async (instrumentId: string) => {
      try {
        await instrumentService.update(instrumentId, { status: 'active' });
        await loadInstruments();
        onInstrumentsChanged?.();
        onChange(instrumentId);
      } catch (err) {
        console.error('Failed to reactivate instrument:', err);
      }
    },
    [loadInstruments, onChange, onInstrumentsChanged]
  );

  /**
   * Handle selection change
   */
  const handleSelectionChange = useCallback(
    (keys: 'all' | Set<React.Key>) => {
      if (keys === 'all') return;

      const selected = Array.from(keys)[0] as string | undefined;
      if (!selected) return;

      // Import action - open import modal
      if (selected === IMPORT_KEY) {
        setIsImportModalOpen(true);
        return;
      }

      // Check if selected instrument is inactive
      const instrument = instruments.find((i) => i.id === selected);
      if (instrument && instrument.status !== 'active') {
        // Open reactivation confirmation modal
        setPendingReactivation({
          instrumentId: selected,
          symbol: instrument.symbol,
          displayName: instrument.displayName,
        });
        return;
      }

      // Normal selection
      onChange(selected);
    },
    [instruments, onChange]
  );

  /**
   * Handle confirmation of reactivation from modal
   */
  const handleConfirmReactivation = useCallback(() => {
    if (pendingReactivation) {
      handleReactivateAndSelect(pendingReactivation.instrumentId);
    }
    setPendingReactivation(null);
  }, [pendingReactivation, handleReactivateAndSelect]);

  /**
   * Handle cancellation of reactivation from modal
   */
  const handleCancelReactivation = useCallback(() => {
    setPendingReactivation(null);
  }, []);

  /**
   * Handle import modal close
   */
  const handleImportModalClose = useCallback(() => {
    setIsImportModalOpen(false);
  }, []);

  /**
   * Handle import completion
   */
  const handleImportComplete = useCallback(
    async (importedIds: string[]) => {
      // Refresh instruments list
      await loadInstruments();

      // Notify parent of changes
      onInstrumentsChanged?.();

      // Close modal
      setIsImportModalOpen(false);

      // If single instrument imported, auto-select it
      if (importedIds.length === 1 && importedIds[0]) {
        onChange(importedIds[0]);
      }
      // If multiple imported, leave selection as-is (user can select from refreshed list)
    },
    [loadInstruments, onInstrumentsChanged, onChange]
  );

  // Determine selected keys (gate on loading and value existence in rendered options)
  // Only check against visible instruments (active + inactive) to avoid "key not in collection" warnings
  const hasValue =
    value &&
    (activeInstruments.some((i) => i.id === value) ||
      inactiveInstruments.some((i) => i.id === value));
  const selectedKeys = loading || !hasValue ? [] : [value];

  // Check if we have any visible instruments (active or inactive, not archived)
  const hasInstruments = activeInstruments.length > 0 || inactiveInstruments.length > 0;

  // Pre-exclude check: do instruments exist in the library at all? (active or inactive, ignoring excludeIds)
  const hasAnyActiveOrInactive = instruments.some(
    (i) => i.status === 'active' || i.status === 'inactive'
  );

  /**
   * Get description for instrument (displayName if different from symbol)
   */
  const getDescription = (instrument: Instrument): string | undefined => {
    if (
      instrument.displayName &&
      instrument.displayName.trim() &&
      instrument.displayName !== instrument.symbol
    ) {
      return instrument.displayName;
    }
    return undefined;
  };

  return (
    <>
    <Select
      label={label}
      placeholder={placeholder}
      selectedKeys={selectedKeys}
      onSelectionChange={handleSelectionChange}
      size={size}
      isDisabled={isDisabled || loading}
      disableAnimation={disableAnimation}
      {...(popoverProps && { popoverProps })}
      {...(className && { className })}
      aria-label={label || 'Instrument selection'}
    >
      {!hasInstruments && !loading
        ? // Empty state - no sections, just items
          [
            <SelectItem key="__no_instruments__" isDisabled>
              {hasAnyActiveOrInactive
                ? 'All instruments already selected'
                : 'No instruments configured'}
            </SelectItem>,
            <SelectItem
              key={IMPORT_KEY}
              startContent={<PackagePlus size={16} className="text-primary" />}
              className="text-primary"
            >
              Import from Catalog...
            </SelectItem>,
          ]
        : // Populated state - grouped sections
          [
            // Asset class sections for active instruments
            ...populatedAssetClasses.map((assetClass) => {
              const assetClassInstruments = groupedInstruments.get(assetClass) || [];

              return (
                <SelectSection
                  key={assetClass}
                  title={ASSET_CLASS_TYPE_DISPLAY_MAP[assetClass]}
                  showDivider
                >
                  {assetClassInstruments.map((instrument) => (
                    <SelectItem
                      key={instrument.id}
                      description={getDescription(instrument)}
                    >
                      {instrument.symbol}
                    </SelectItem>
                  ))}
                </SelectSection>
              );
            }),
            // Inactive instruments section (if any)
            ...(inactiveInstruments.length > 0
              ? [
                  <SelectSection key="__inactive__" title="Inactive" showDivider>
                    {inactiveInstruments.map((instrument) => (
                      <SelectItem
                        key={instrument.id}
                        description={getDescription(instrument)}
                        className="text-default-400"
                      >
                        {instrument.symbol}
                      </SelectItem>
                    ))}
                  </SelectSection>,
                ]
              : []),
            // Import section
            <SelectSection key="__import_section__">
              <SelectItem
                key={IMPORT_KEY}
                startContent={<PackagePlus size={16} className="text-primary" />}
                className="text-primary"
              >
                Import from Catalog...
              </SelectItem>
            </SelectSection>,
          ]}
    </Select>

    {/* Reactivation confirmation modal */}
    <ReactivateInstrumentConfirmationModal
      isOpen={pendingReactivation !== null}
      onClose={handleCancelReactivation}
      onConfirm={handleConfirmReactivation}
      instrumentSymbol={pendingReactivation?.symbol ?? ''}
      instrumentDisplayName={pendingReactivation?.displayName}
    />

    {/* Import from catalog modal */}
    <InstrumentImportModal
      isOpen={isImportModalOpen}
      onClose={handleImportModalClose}
      onImportComplete={handleImportComplete}
      selectionMode="single"
    />
  </>
  );
};

export default InstrumentDropdown;
